import { unwrap } from "idb";
import { RecordableEvent, RecordableEventCategory, RecordableEventName } from "../../lib/Events";
import { SerializableError } from "../../lib/utils/SerializedException";
import { ProcessInfo } from "../ProcessInfo";
import { type DbRecordedEvent, NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, DbRecordedEventByCategoryAndName, NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName, NameOfIndexForReceivedEventUuidThenEventName, DbRecordedEventByName, NameOfIndexForEventListenerUuidThenEventName, UUID } from "./DatabaseTypes";
import { EvidenceDatabase } from "./EvidenceDbStruct";
import { requireEvidenceDatabase } from "./getDatabase";

export class DbEventsTableReader {

	getEvents = async (startAtIndexInclusive: number = 0, count?: number) => {
		const {db} = this;
		const events = await db.getAll("events",
			IDBKeyRange.bound([this.recordingUuid, startAtIndexInclusive], [this.recordingUuid, Number.MAX_SAFE_INTEGER], false, false),
			count
		);
		return events;
	} 

	numberOfEvents = async () => (await this.db).count("events", IDBKeyRange.only([this.recordingUuid]));


	forEvents = async (startAtIndexInclusive: number, callback: (event: DbRecordedEvent, {halt}: {halt: () => void}) => unknown | Promise<unknown>) => {
		const chunkSize = 250;
		let events: DbRecordedEvent[] | undefined;
		let shouldHalt = false;
		const halt = () => shouldHalt = true;
		// If memory allows, just load all the events in one go and iterate over them
		do {
			// const beforeLoad = performance.now();
			events = await this.getEvents(startAtIndexInclusive, chunkSize);
			// const loadLatency = (performance.now() - beforeLoad);
			if (events == null || events.length === 0) return;
			// ProcessInfo.log(`Loaded ${events.length} events of size ${roughSizeOfObject(events).toLocaleString('en',{useGrouping: 'always'})} bytes in ${loadLatency.toLocaleString('en',{useGrouping: 'always'})} ms`);
			// ProcessInfo.log(`Body response size: ${roughSizeOfObject(events.filter( e => e.category==='Debugger.Network' && e.eventName === 'Network.getResponseBodyResponse')).toLocaleString('en',{useGrouping: 'always'})} bytes`);
			for (const event of events) {
				try {
					await callback(event, {halt});
				} catch (e) {
					ProcessInfo.errorLog("Error processing event", event, SerializableError.from(e, 'forEvents'));
				}
			}
			startAtIndexInclusive = events[events.length - 1]!.eventIndex + 1;
		} while (events != null && events.length >= chunkSize && !shouldHalt);
	};

	/**
	 * 
	 * @param callback Must be synchronous and return "undefined" to ensure promises aren't accidentally called
	 */
	forEventsWithCategoryAndName =  <
		EVENT_CATEGORY extends RecordableEventCategory,
		EVENT_NAME extends RecordableEventName
	>(
		category: EVENT_CATEGORY,
		name: EVENT_NAME,
		lowestEventIndexInclusive: number = 0
	)  => async (
		callback: (event: DbRecordedEventByCategoryAndName<EVENT_CATEGORY, EVENT_NAME>) => undefined
	): Promise<void> => {
		const tx = (await this.db).transaction("events", "readonly");
		const index = tx.store.index(NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex);
		const bound = IDBKeyRange.bound([this.recordingUuid, category, name, lowestEventIndexInclusive], [this.recordingUuid, category, name, Number.MAX_SAFE_INTEGER], false, false);
		for await (const cursor of index.iterate(bound)) {
			callback(cursor.value as DbRecordedEventByCategoryAndName<EVENT_CATEGORY, EVENT_NAME>);
		}
		tx.commit();
		await tx.done;
	};

	countEventsOfType = async <
		EVENT_CATEGORY extends RecordableEventCategory,
		EVENT_NAME extends RecordableEventName
	>(category: EVENT_CATEGORY, name: EVENT_NAME): Promise<number> => {
		const bound = IDBKeyRange.bound([this.recordingUuid, category, name, 0], [this.recordingUuid, category, name, Number.MAX_SAFE_INTEGER], false, false);
		return (await this.db).countFromIndex("events", NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, bound);
	};

	getEvent = async ({eventIndex}: {eventIndex: number}) =>
		await this.db.get("events", [this.recordingUuid, eventIndex]);

	getEventsByCategoryThenNameThenRequest = <
		CATEGORY extends RecordableEventCategory,
		NAME extends RecordableEventName
	>(
		category: CATEGORY,
		name: NAME
	) => async (requestId: string) => {
		try {
			return	await ((await this.db).getAllFromIndex(
				"events",
				NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName,  [this.recordingUuid, requestId, category, name]
			) as Promise<DbRecordedEventByCategoryAndName<CATEGORY, NAME>[]>);
		} catch (e) {
			ProcessInfo.errorLog("Failed to get event by category, name, and request ID", [this.recordingUuid, requestId, category, name], SerializableError.from(e, 'getEventByCategoryThenNameThenRequest'));
			throw e;
		}
	}

	getEventsForCategoryAndName = async <CATEGORY extends DbRecordedEvent["category"], NAME extends DbRecordedEvent["eventName"]>(
		{category, name,
			startingAtIndexExclusive = Number.MIN_SAFE_INTEGER,
			startingAtIndexInclusive = startingAtIndexExclusive + (startingAtIndexExclusive < Number.MIN_SAFE_INTEGER ? 1 : 0),
		}:
		{category: CATEGORY, name: NAME, startingAtIndexInclusive?: number, startingAtIndexExclusive?: number}
	) => {
		const bound = IDBKeyRange.bound(
			[this.recordingUuid, category, name, startingAtIndexInclusive],
			[this.recordingUuid, category, name, Number.MAX_SAFE_INTEGER],
			false, false);
		const {db} = this;
		return await db.getAllFromIndex(
			"events", NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, bound
		) as DbRecordedEventByCategoryAndName<CATEGORY, NAME>[];
	}

	getEventWithReceivedEventUuid = async <EVENT_NAME extends Extract<RecordableEvent, {raw: {receivedEventUuid: UUID}}>["eventName"]>(
		receivedEventUuid: UUID, eventName: EVENT_NAME
	) => {
		const bound = IDBKeyRange.only([receivedEventUuid, eventName]);
		const {db} = this;
		return await db.getFromIndex(
			"events", NameOfIndexForReceivedEventUuidThenEventName, bound
		) as DbRecordedEventByName<typeof eventName> | undefined;
	}

	getEventWithEventListenerUuid = async <EVENT_NAME extends Extract<RecordableEvent, {raw: {eventListenerUuid: UUID}}>["eventName"]>(
		eventListenerUuid: UUID,
		eventName: EVENT_NAME
	) => {
		const bound = IDBKeyRange.only([eventListenerUuid, eventName]);
		const {db} = this;
		return await db.getFromIndex(
			"events", NameOfIndexForEventListenerUuidThenEventName, bound
		) as DbRecordedEventByName<typeof eventName> | undefined
	}

	readonly idbDatabase: IDBDatabase;
	protected constructor(public readonly db: EvidenceDatabase, public readonly recordingUuid: UUID) {
		this.idbDatabase = unwrap(db);
	}

	static #forRecordingUuid = new Map<UUID, DbEventsTableReader>();
	static forRecordingUuid = (recordingUuid: UUID) => DbEventsTableReader.#forRecordingUuid.get(recordingUuid) ?? (async() => {
		const evidenceDbForRecording = new DbEventsTableReader(await requireEvidenceDatabase(), recordingUuid);
		DbEventsTableReader.#forRecordingUuid.set(recordingUuid, evidenceDbForRecording);
		return evidenceDbForRecording;
	})();
	static forDbAndRecordingUuid = (db: EvidenceDatabase, recordingUuid: UUID) => DbEventsTableReader.#forRecordingUuid.get(recordingUuid) ?? (() => {
		const evidenceDbForRecording = new DbEventsTableReader(db, recordingUuid);
		DbEventsTableReader.#forRecordingUuid.set(recordingUuid, evidenceDbForRecording);
		return evidenceDbForRecording;
	})();

	static removeRecordingUuid = (recordingUuid: UUID) => DbEventsTableReader.#forRecordingUuid.delete(recordingUuid);


}


